import { useInfiniteQuery } from '@tanstack/react-query'

import axiosInstance from '@/libs/configs/axios'

export const WITHDRAW_LIST_KEY = 'get_list_reward_history'

export enum ERewardHistoryType {
  CASH = 'CASH',
  BANK_WITHDRAWAL = 'BANK_WITHDRAWAL',
  EWALLET_WITHDRAWAL = 'EWALLET_WITHDRAWAL',
  ADMIN_FEE = 'ADMIN_FEE',
}

export enum ERewardHistoryStatus {
  SUCCESS = 'SUCCESS',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
}

export enum EWithdrawalType {
  MANUAL_WITHDRAWAL = 'Manual Withdrawal',
  AUTO_WITHDRAWAL = 'Auto Withdrawal',
}

export type TListRewardHistoryPayload = {
  pageParam: number
}

export type TRewardHistoryData = {
  id: string
  amount: number
  type: ERewardHistoryType
  remarks: string
  status: ERewardHistoryStatus
  createdAt: Date
  updatedAt: Date
  withdrawalType: EWithdrawalType
  providerName: string
  maskedAccountNumber: string
}

export type TListRewardHistoryResult = {
  success: boolean
  traceId: string
  error?: {
    code: string
    message: string
    errors: any[]
  }
  meta: {
    page: number
    limit: number
    total: number
  }
  result: {
    data: TRewardHistoryData[]
  }
}

async function getListRewardHistoryAPI(
  { pageParam }: TListRewardHistoryPayload,
  partnerId?: string,
  useMockServer: boolean = false,
): Promise<TListRewardHistoryResult> {
  try {
    const result = await axiosInstance({
      baseURL: useMockServer
        ? process.env.NEXT_PUBLIC_MOCK_API_URL
        : process.env.NEXT_PUBLIC_API_URL,
      url: `/reward`,
      method: 'get',
      params: {
        limit: 10,
        page: pageParam,
        ...(partnerId && { partner_id: partnerId }),
      },
    })

    return result?.data
  } catch (error: any) {
    throw error?.response?.data
  }
}

export default function useGetListRewardHistoryQuery({
  enabled,
  partnerId,
}: {
  enabled: boolean
  partnerId?: string
}) {
  const {
    data,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isFetching,
  } = useInfiniteQuery({
    queryKey: [WITHDRAW_LIST_KEY, partnerId],
    queryFn: ({ pageParam }) => getListRewardHistoryAPI({ pageParam }, partnerId),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage?.meta?.page + 1
      return nextPage <= Math.ceil(lastPage?.meta?.total / lastPage?.meta?.limit)
        ? nextPage
        : undefined
    },
    enabled,
  })

  return {
    data,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isFetching,
  }
}
